import type { EngageAccountDto, EngageConversationDto } from "@bafe/api-client";
import type { QueryClient } from "@tanstack/react-query";

import { queryKeys } from "./query.js";

type EngageLiveFrame = {
	type?: string;
	accountId?: string;
	status?: string;
	conversationId?: string;
};

/**
 * Conversation frames are read, pin, assign, note and delete — not a new
 * message. Bumping lastMessageAt on those would send the open chat to the
 * top, then the refetch would drop it back.
 */
export function applyEngageLiveFrame(queryClient: QueryClient, raw: string): void {
	let payload: EngageLiveFrame = {};
	try {
		payload = JSON.parse(raw) as EngageLiveFrame;
	} catch {
		payload = {};
	}

	if (payload.type === "message" && payload.conversationId) {
		const conversationId = payload.conversationId;
		const now = new Date().toISOString();
		queryClient.setQueryData(queryKeys.engageConversations, (rows: EngageConversationDto[] | undefined) =>
			(rows ?? []).map((row) => (row.id === conversationId ? { ...row, lastMessageAt: now } : row)),
		);
		void queryClient.invalidateQueries({ queryKey: queryKeys.engageConversations });
		void queryClient.invalidateQueries({ queryKey: queryKeys.engageMessages(conversationId) });
		return;
	}

	if (payload.type === "conversation") {
		void queryClient.invalidateQueries({ queryKey: queryKeys.engageConversations });
		return;
	}

	if (
		payload.type === "account" &&
		payload.accountId &&
		(payload.status === "expired" || payload.status === "disconnected")
	) {
		const accountId = payload.accountId;
		const drop = payload.status === "disconnected";
		queryClient.setQueryData(queryKeys.engageConversations, (rows: EngageConversationDto[] | undefined) =>
			(rows ?? []).filter((row) => row.accountId !== accountId),
		);
		queryClient.setQueryData(queryKeys.engageAccounts, (rows: EngageAccountDto[] | undefined) =>
			drop
				? (rows ?? []).filter((row) => row.id !== accountId)
				: (rows ?? []).map((row) => (row.id === accountId ? { ...row, status: "expired" } : row)),
		);
	}

	if (payload.type === "account") {
		void queryClient.invalidateQueries({ queryKey: queryKeys.engageConversations });
		void queryClient.invalidateQueries({ queryKey: queryKeys.engageAccounts });
	}
}
